// src/components/Blocks/SocialMeta/View.jsx

import React from 'react';
import { Helmet } from '@plone/volto/helpers';

const SocialMetaView = (props) => {
  const { data, content } = props;

  // Helper function to process image objects
  const processImageObject = (imageObj) => {
    if (!imageObj) return null;
    
    // Try scales first
    if (imageObj.scales?.large?.download) {
      return imageObj.scales.large.download;
    }
    
    if (imageObj.scales?.preview?.download) {
      return imageObj.scales.preview.download;
    }
    
    // Try download property
    if (imageObj.download) {
      return imageObj.download;
    }
    
    // Construct @@images URL properly
    if (imageObj['@id']) {
      const originalId = imageObj['@id'];
      return `${originalId}/@@images/image/large`;
    }
    
    return null;
  };

  // Get image URL for meta tags
  const getImageUrl = (imageData) => {
    if (!imageData) return null;
    
    // Handle string URLs
    if (typeof imageData === 'string') return imageData;
    
    // Handle arrays (object_browser format)
    if (Array.isArray(imageData) && imageData.length > 0) {
      return processImageObject(imageData[0]);
    }
    
    // Handle direct objects (like lead images from content)
    if (typeof imageData === 'object' && imageData !== null) {
      return processImageObject(imageData);
    }
    
    return null;
  };

  // Generate social meta based on block data and page content
  const generateSocialMeta = () => {
    let socialTitle, socialDescription, socialImage;

    // Determine title and description
    switch (data.social_use_defaults) {
      case 'global':
        socialTitle = data.social_global_default_title;
        socialDescription = data.social_global_default_description;
        break;
      case 'custom':
        socialTitle = data.social_custom_title;
        socialDescription = data.social_custom_description;
        break;
      default: // 'page'
        socialTitle = content?.title;
        socialDescription = content?.description;
    }

    // Determine image
    switch (data.social_image_source) {
      case 'globalDefault':
        socialImage = data.social_global_default_image;
        break;
      case 'custom':
        socialImage = data.social_custom_image;
        break;
      default: // 'leadImage'
        socialImage = content?.image || content?.preview_image || data.social_global_default_image;
    }

    return {
      title: socialTitle || data.social_global_default_title || content?.title,
      description: socialDescription || data.social_global_default_description || content?.description,
      image: socialImage,
      siteName: data.social_global_site_name,
      twitterHandle: data.social_global_twitter_handle,
      twitterCard: data.social_twitter_card || 'summary_large_image',
    };
  };

  const socialMeta = generateSocialMeta();
  const imageUrl = getImageUrl(socialMeta.image);

  // Get current page URL
  const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
  const currentDomain = typeof window !== 'undefined' ? window.location.hostname : '';

  return (
    <>
      <Helmet>
        {/* Facebook/Open Graph Meta Tags */}
        <meta property="og:url" content={currentUrl} />
        <meta property="og:type" content="website" />
        {socialMeta.title && (
          <meta property="og:title" content={socialMeta.title} />
        )}
        {socialMeta.description && (
          <meta property="og:description" content={socialMeta.description} />
        )}
        {imageUrl && (
          <meta property="og:image" content={imageUrl} />
        )}
        {socialMeta.siteName && (
          <meta property="og:site_name" content={socialMeta.siteName} />
        )}
        
        {/* Twitter Meta Tags */}
        <meta name="twitter:card" content={socialMeta.twitterCard} />
        <meta property="twitter:domain" content={currentDomain} />
        <meta property="twitter:url" content={currentUrl} />
        {socialMeta.title && (
          <meta name="twitter:title" content={socialMeta.title} />
        )}
        {socialMeta.description && (
          <meta name="twitter:description" content={socialMeta.description} />
        )}
        {imageUrl && (
          <meta name="twitter:image" content={imageUrl} />
        )}
        {socialMeta.twitterHandle && (
          <meta name="twitter:site" content={socialMeta.twitterHandle} />
        )}
      </Helmet>
      
      {/* This block renders nothing visible on the page */}
      <div style={{ display: 'none' }} className="social-meta-block" />
    </>
  );
};

export default SocialMetaView;